import { useState } from "react";
import { useAuthStore } from "@store/auth";
import { raiseError } from "context/error.service";
import { GoogleIcon } from "@components/ui/icons/ProviderIcons";

// ─── Splash → Login flow ────────────────────────────────────

export default function MobileLoginScreen() {
  const [step, setStep] = useState<"welcome" | "login">("welcome");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const login = useAuthStore((s) => s.login);
  const loginWithGoogle = useAuthStore((s) => s.loginWithGoogle);

  const handleLogin = async () => {
    if (!email || !password) return;
    try {
      setIsLoading(true);
      await login(email, password, true);
    } catch (error: any) {
      raiseError({
        type: "Erreur de connexion",
        reason: error?.code === "auth/invalid-credential"
          ? "Email ou mot de passe incorrect."
          : "La connexion a échoué.",
        solutions: ["Vérifiez vos identifiants.", "Réessayez."],
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setIsGoogleLoading(true);
      await loginWithGoogle();
    } catch (error: any) {
      if (error?.code !== "auth/popup-closed-by-user" && error?.code !== "auth/cancelled-popup-request") {
        raiseError({
          type: "Erreur Google",
          reason: "La connexion avec Google a échoué.",
          solutions: ["Réessayez."],
        });
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  // ─── Welcome screen ───────────────────────────────────────

  if (step === "welcome") {
    return (
      <div className="flex flex-col h-screen bg-black relative" style={{ fontFamily: "Poppins" }}>
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="/welcome.jpg"
            alt=""
            className="w-full h-full object-cover"
            style={{ opacity: 0.5 }}
          />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 71%, #000000 100%)" }}
          />
        </div>

        {/* Content */}
        <div className="relative flex flex-col flex-1 justify-end items-center" style={{ padding: "10px 20px", gap: 32 }}>
          <span
            style={{
              fontFamily: "Satoshi, Poppins, sans-serif",
              fontWeight: 900,
              fontSize: 36,
              lineHeight: "49px",
              letterSpacing: "-0.01em",
              color: "#FFFFFF",
              textAlign: "center",
            }}
          >
            Carvis®
          </span>

          <button
            onClick={() => setStep("login")}
            className="w-full flex items-center justify-center"
            style={{ height: 50, background: "#FFFFFF", borderRadius: 30 }}
          >
            <span style={{ fontWeight: 500, fontSize: 14, color: "#000000" }}>
              Commencer
            </span>
          </button>

          <div style={{ width: 104, height: 5, background: "#FFFFFF", borderRadius: 30 }} />
        </div>
      </div>
    );
  }

  // ─── Login screen ─────────────────────────────────────────

  return (
    <div className="flex flex-col h-screen bg-black relative" style={{ fontFamily: "Poppins" }}>
      {/* Background top */}
      <div className="relative flex-shrink-0" style={{ height: 280 }}>
        <img
          src="/welcome.jpg"
          alt=""
          className="w-full h-full object-cover"
          style={{ filter: "blur(20px)" }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 68%, #000000 100%)" }}
        />
      </div>

      {/* Form area */}
      <div
        className="flex flex-col flex-1 items-center"
        style={{ padding: "10px 20px", gap: 32, backdropFilter: "blur(2.9px)", borderRadius: 30, marginTop: -30 }}
      >
        {/* Title */}
        <div className="flex flex-col w-full" style={{ gap: 5 }}>
          <span style={{ fontWeight: 500, fontSize: 24, lineHeight: "36px", color: "#FFFFFF" }}>
            Identifiez-vous
          </span>
          <span style={{ fontWeight: 500, fontSize: 14, lineHeight: "21px", color: "#D9D9D9" }}>
            Entrez votre email et mot de passe.
          </span>
        </div>

        {/* Fields */}
        <div className="flex flex-col w-full" style={{ gap: 15 }}>
          {/* Email */}
          <div className="flex flex-col" style={{ gap: 4 }}>
            <span style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#A8A8A8" }}>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jean.mat@gmail.com"
              className="w-full outline-none"
              style={{
                padding: 15, height: 48,
                border: "1px solid #A1A1A1", borderRadius: 30,
                fontFamily: "Poppins", fontWeight: 500, fontSize: 12,
                color: "#FFFFFF", background: "transparent",
              }}
            />
          </div>

          {/* Password */}
          <div className="flex flex-col" style={{ gap: 4 }}>
            <span style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#A8A8A8" }}>Mot de passe</span>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                className="w-full outline-none"
                style={{
                  padding: "15px 48px 15px 15px", height: 48,
                  border: "1px solid #A1A1A1", borderRadius: 30,
                  fontFamily: "Poppins", fontWeight: 500, fontSize: 12,
                  color: "#FFFFFF", background: "transparent",
                }}
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="absolute flex items-center justify-center"
                style={{ right: 15, top: "50%", transform: "translateY(-50%)" }}
              >
                <svg width={14} height={9} viewBox="0 0 14 9" fill="none">
                  <path d="M7 0C3.5 0 0.5 3.5 0.5 4.5S3.5 9 7 9s6.5-3.5 6.5-4.5S10.5 0 7 0z" stroke="#A8A8A8" strokeWidth={1} fill="none" />
                  <circle cx={7} cy={4.5} r={2} stroke="#A8A8A8" strokeWidth={1} fill="none" />
                </svg>
              </button>
            </div>
          </div>

          {/* Forgot password */}
          <span style={{ fontWeight: 500, fontSize: 13, lineHeight: "20px", color: "#FFFFFF", textDecoration: "underline", cursor: "pointer" }}>
            Mot de passe oublié ?
          </span>
        </div>

        {/* Buttons */}
        <div className="flex flex-col w-full" style={{ gap: 10 }}>
          {/* Login button */}
          <button
            onClick={handleLogin}
            disabled={isLoading}
            className="w-full flex items-center justify-center"
            style={{ height: 50, background: "#FFFFFF", borderRadius: 30, opacity: isLoading ? 0.7 : 1 }}
          >
            <span style={{ fontWeight: 500, fontSize: 14, color: "#000000" }}>
              {isLoading ? "Connexion..." : "Se Connecter"}
            </span>
          </button>

          {/* Divider */}
          <div className="flex flex-row items-center" style={{ gap: 9 }}>
            <div className="flex-1" style={{ height: 0, border: "1px solid #D9D9D9" }} />
            <span style={{ fontWeight: 500, fontSize: 10, color: "#D9D9D9" }}>ou</span>
            <div className="flex-1" style={{ height: 0, border: "1px solid #D9D9D9" }} />
          </div>

          {/* Google */}
          <button
            onClick={handleGoogleLogin}
            disabled={isGoogleLoading}
            className="w-full flex items-center justify-center"
            style={{ height: 50, background: "#2D2D2D", borderRadius: 30, gap: 10, opacity: isGoogleLoading ? 0.7 : 1 }}
          >
            <GoogleIcon size={20} />
            <span style={{ fontWeight: 500, fontSize: 12, color: "#FFFFFF" }}>
              {isGoogleLoading ? "Connexion..." : "Continuer avec Google"}
            </span>
          </button>

          {/* Apple */}
          <button
            className="w-full flex items-center justify-center"
            style={{ height: 50, background: "#2D2D2D", borderRadius: 30, gap: 10 }}
          >
            <svg width={20} height={20} viewBox="0 0 20 20" fill="none">
              <path d="M14.94 10.27c-.02-2.18 1.79-3.24 1.87-3.29-1.02-1.49-2.6-1.69-3.16-1.72-1.34-.14-2.63.8-3.31.8-.69 0-1.74-.78-2.87-.76-1.47.02-2.84.87-3.6 2.2-1.54 2.68-.39 6.65 1.1 8.82.74 1.06 1.61 2.25 2.75 2.21 1.11-.05 1.53-.71 2.87-.71 1.33 0 1.72.71 2.88.68 1.19-.02 1.94-1.08 2.66-2.15.84-1.23 1.19-2.42 1.21-2.48-.03-.01-2.32-.89-2.34-3.53l-.06-.07zM12.74 3.64c.6-.74 1.01-1.76.9-2.79-.87.04-1.93.59-2.55 1.32-.56.64-1.05 1.68-.92 2.67.97.08 1.97-.5 2.57-1.2z" fill="#FFFFFF"/>
            </svg>
            <span style={{ fontWeight: 500, fontSize: 12, color: "#FFFFFF" }}>
              Continuer avec Apple
            </span>
          </button>
        </div>
      </div>

      {/* Bottom indicator */}
      <div className="flex justify-center" style={{ padding: "10px 0 8px" }}>
        <div style={{ width: 104, height: 5, background: "#FFFFFF", borderRadius: 30 }} />
      </div>
    </div>
  );
}
